import type { Survey } from '@/types/types'
import type { CustomVars, FormattedBlock } from './interfaces.ts'

export const convertBlocksToText = (
  formattedBlocks: FormattedBlock[],
  fromExample?: boolean,
  varList?: CustomVars[]
) => {
  const parsedText = formattedBlocks.reduce((acc, block) => {
    if (!block.isVar && block.textValue) {
      return acc + block.textValue
    }
    if (block.isVar && block.textValue) {
      const varItem = findInVarListFromCode(block.textValue, varList)

      if (fromExample && varItem?.example) {
        return `${acc}${varItem.example}`
      }

      return `${acc}{{ ${block.textValue} }}`
    }
    return acc
  }, '')
  return parsedText
}

export const convertTextToBlocks = (text: string): FormattedBlock[] => {
  if (!text) {
    return [{ textValue: ' ', isVar: false }]
  }

  const blocks: FormattedBlock[] = []
  let currentBlock: FormattedBlock = { textValue: '', isVar: false }

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    const nextChar = text[i + 1]

    if (char === '{' && nextChar === '{') {
      if (currentBlock.textValue) {
        blocks.push(currentBlock)
      }
      currentBlock = { textValue: '', isVar: true }
      i++
    } else if (char === '}' && nextChar === '}') {
      if (currentBlock.textValue) {
        blocks.push({ ...currentBlock, textValue: currentBlock.textValue.trim() })
      }
      currentBlock = { textValue: '', isVar: false }
      i++
    } else {
      currentBlock.textValue += char
    }
  }

  if (currentBlock.textValue) {
    blocks.push(currentBlock)
  }

  return blocks
}

export const getPreviewText = (text: string, customVars: CustomVars[]) => {
  const blocks = convertTextToBlocks(text)
  return convertBlocksToText(blocks, true, customVars)
}

export const buildResolvedCustomVarCode = (customVar: CustomVars, rawValue: string): string => {
  if (customVar.customValueType === 'pageId' && customVar.customValueModel === 'survey') {
    const m = customVar.code.match(/^(.+)\(([^)]+)\)$/)
    if (m) {
      return `${m[1]}(${m[2]}/${rawValue})`
    }
  }

  return `${customVar.code}(${rawValue})`
}

export const extractCustomValue = (text: string, customVars: CustomVars[]) => {
  const cleanedText = text
    .trim()
    .replace(/^\{\{\s*/u, '')
    .replace(/\s*\}\}$/u, '')
  const resolvedFromFull = findInVarListFromCode(cleanedText, customVars)

  if (resolvedFromFull?.customValueType === 'pageId' && resolvedFromFull.customValueModel === 'survey') {
    const fullMatch = cleanedText.match(/^(.+)\(([^)]+)\)$/)
    const inner = fullMatch?.[2] ?? ''
    const idMatch = inner.match(/^survey\/(\d+)$/)
    return {
      value: idMatch ? idMatch[1] : null,
      varCode: fullMatch?.[1] ?? null,
      customVar: resolvedFromFull
    }
  }

  const match = cleanedText.match(/(.*?)\(([\s\S]*)\)/)

  if (match) {
    const varCode = match[1]
    const value = match[2]
    const customVar = findInVarListFromCode(varCode, customVars)
    if (customVar) {
      return { value, varCode, customVar }
    }
  }

  return {
    value: null,
    varCode: null,
    customVar: null
  }
}

export const removeDoubleSpacesAroundVars = (text: string) => {
  return text.replaceAll('  {{', ' {{').replaceAll('}}  ', '}} ')
}

export const ensureOneSpaceInsideVars = (text: string) => {
  // First, check if any changes are needed without modifying the string
  const needsFixing =
    /\{\{(?!\s[^}\s]+\s\}\})/.test(text) || /\{\{\s[^}]+?\s{2,}\}\}/.test(text) || /\{\{\s{2,}[^}]+?\s\}\}/.test(text)

  // If no changes needed, return original string to maintain reference equality
  if (!needsFixing) {
    return text
  }

  // Only perform replacements if changes are actually needed
  let result = text

  // Fix variables with no spaces: {{variable}} -> {{ variable }}
  result = result.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
    if (variable.startsWith(' ') && variable.endsWith(' ') && variable.trim().indexOf(' ') === -1) {
      return match // Already correctly formatted
    }
    return `{{ ${variable.trim()} }}`
  })

  // Fix variables with multiple spaces: {{  variable  }} -> {{ variable }}
  result = result.replace(/\{\{\s([^}]+?)\s{2,}\}\}/g, '{{ $1 }}')
  result = result.replace(/\{\{\s{2,}([^}]+?)\s\}\}/g, '{{ $1 }}')

  return result
}

export const countTextChar = (text: string, sanitizeForGsm: boolean = false) => {
  if (sanitizeForGsm) {
    const doubleCharLength = ['\\', '[', ']', '{', '}', '~', '^', '|', '€']
    return text?.split('')?.reduce((acc, char) => {
      if (doubleCharLength.includes(char)) {
        return acc + 2
      }
      return acc + 1
    }, 0)
  }
  return text.length
}

export const countTotalCharacters = ({
  textModelValue,
  varList,
  sanitizeForGsm = false
}: {
  textModelValue: string
  varList: CustomVars[]
  sanitizeForGsm?: boolean
}) => {
  const formattedBlocks = convertTextToBlocks(textModelValue.trim())

  return formattedBlocks.reduce((acc, block) => {
    if (block.isVar === false) {
      return acc + countTextChar(block?.textValue, sanitizeForGsm)
    }

    if (block.isVar === true) {
      const varItem = findInVarListFromCode(block.textValue, varList)

      if (varItem) {
        return acc + varItem.length
      }

      const { value, customVar } = extractCustomValue(block.textValue, varList)

      if (value && customVar?.customValueType === 'link') {
        return acc + customVar.length
      }

      return acc + countTextChar(`{{ ${block?.textValue} }}`, sanitizeForGsm)
    }
  }, 0)
}

export const isValidUrl = (url: string): boolean => {
  const urlRegex =
    /^(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,24}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/gi

  const val = url.trim()
  if (!val || /\s/.test(val)) return false
  if (/\.{2,}/.test(val)) return false

  return urlRegex.test(val)
}

export const isInVarList = ({
  varCode,
  customVars,
  surveys,
  requiredStoreIds
}: {
  varCode: string
  customVars: CustomVars[]
  surveys?: Survey[]
  requiredStoreIds?: number[]
}) => {
  const code = varCode?.startsWith('customerApp(survey/') ? varCode.replace(/\/\d+/g, '') : varCode
  const found = findInVarListFromCode(code, customVars)
  if (!found) return false

  if (found.customValueType === 'link') {
    const { value } = extractCustomValue(code, customVars)
    if (!value) return false
    if (found.code === 'customLink') {
      return isValidUrl(value)
    }
  }

  if (found.customValueType === 'pageId' && found.customValueModel === 'survey') {
    const m = varCode.match(/^(.+)\(([^)]+)\)$/)

    if (!m || !surveys?.length) return false

    const surveyId = Number(m[2].split('/')[1])
    const surveyIds = surveys?.map((survey) => survey.id)
    const isInSurvey = surveyIds?.includes(surveyId)

    if (!requiredStoreIds?.length) return isInSurvey

    const survey = surveys?.find((survey) => survey.id === surveyId)
    const hasSurveyRequiredStores = requiredStoreIds?.every((id) => survey?.stores?.some((store) => store.id === id))

    return isInSurvey && hasSurveyRequiredStores
  }

  return true
}

export const findInVarListFromCode = (varCode: string, customVars: CustomVars[]) => {
  const code = varCode?.startsWith('customerApp(survey/') ? varCode.replace(/\/\d+/g, '') : varCode

  return customVars?.find(
    (i) =>
      i.code.toLowerCase() === code.toLowerCase() || (i.customValueType === 'link' && hasCustomVarDefined(code, i.code))
  )
}

export const extractSurveyIdsFromMessageContent = (messageContent: string): number[] => {
  if (!messageContent) return []
  const re = /{{\s*customerApp\(survey\/([\d]+)\)\s*}}/g
  return [...messageContent.matchAll(re)].map((m) => Number(m[1]))
}

export const hasEmojiCharacters = (string: string) => {
  return /[^\p{L}\p{N}\p{P}\p{Z}\p{M}\p{Sc}^$<>=+¨~−\n]/gu.test(string)
}

export const hasCustomVarDefined = (string: string, code: string) => {
  return string.match(new RegExp(`${code}(.*?)`, 'i'))?.length >= 1
}

export const addHttpsToUrl = (url: string) => {
  if (!url) return

  if (!url.startsWith('http')) {
    return `https://${url}`
  }
  return url
}

export const getCustomLinks = (text: string) => {
  const urlRegex = /\{\{\s*customLink\((.*?)\)\s*\}\}/gi
  const matches = text.match(urlRegex)
  return matches?.map((match) => match.replace(urlRegex, '$1'))
}

export const wrapUrlsWithCustomLink = (text: string): string => {
  // Regex to detect URLs - matches http(s) URLs and URLs without protocol
  const urlRegex = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,24}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/gi

  let result = ''
  let lastIndex = 0

  let match: RegExpExecArray | null

  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[0]

    if (/\.{2,}/.test(url)) {
      continue
    }

    // Skip false positives like "gambas.et" (where ".et" is a valid TLD but
    // the surrounding context is plain French text). Only wrap when there's
    // an explicit signal: protocol, "www." prefix, or a path after the TLD.
    const hasProtocol = /^https?:\/\//i.test(url)
    const hasWww = /^www\./i.test(url)
    const hasPath = /\.[a-z]{2,24}\//i.test(url)
    if (!hasProtocol && !hasWww && !hasPath) {
      continue
    }

    const startPosition = match.index
    const afterPosition = startPosition + url.length

    const textBefore = text.substring(startPosition - '{{ customLink('.length, startPosition)
    const textAfter = text.substring(afterPosition, afterPosition + ') }}'.length)
    const isPartOfCustomLink = /\{\{\s*customLink\(/.test(textBefore) && /\)\s*}}/.test(textAfter)

    result += text.substring(lastIndex, startPosition)

    if (isPartOfCustomLink) {
      result += url
    } else {
      result += `{{ customLink(${addHttpsToUrl(url)}) }}`
    }

    lastIndex = afterPosition
  }

  result += text.substring(lastIndex)

  return result
}
